import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import { HEM_COMMERCE_BASE_PATH } from 'gatsby-theme-hem-store/src/constants';
import { usePlaceholder } from 'hem-hooks';
import { Button, Input, LoadingIndicator, transition } from 'hem-ui';
import Cookies from 'js-cookie';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import React, { FormEvent, useEffect, useState } from 'react';

import { useCustomer } from '@/hooks/useCustomer';
import { useIPLocation } from '@/hooks/useIPLocation';
import { getAudience } from '@/utils/getAudience';
import { getUserId } from '@/utils/getUserId';
import { sendGtmNewsletterEvent } from '@/utils/sendGtmEvents';

import { form, root } from './NewsletterForm.module.scss';

enum State {
  IDLE,
  LOADING,
  SUCCESS,
  ERROR,
}

export const NewsletterForm: React.FC<{
  tags?: string[];
  onSuccess?: () => void;
  buttonLabel?: string;
  location: string;
  reportEmail: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  tags = [],
  onSuccess,
  buttonLabel = 'Sign up',
  location,
  reportEmail,
}) => {
  const { customer } = useCustomer();
  const placeholder = usePlaceholder();
  const ip = useIPLocation();
  const [name, setName] = useState(customer?.firstName || '');
  const [email, setEmail] = useState(customer?.email || '');
  const [state, setState] = useState<State>(State.IDLE);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setState(State.LOADING);

    try {
      const utmTags = Cookies.get('utmTags');
      if (utmTags !== '') {
        tags.push(utmTags);
      }

      if (ip?.status === 'success' && isString(ip?.data?.country)) {
        tags.push(ip.data.country);
      }

      const res = await fetch(
        `${HEM_COMMERCE_BASE_PATH}/customer_mailing_list/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            audience: getAudience(),
            email: email,
            name: name,
            tags: tags.filter(Boolean),
            userid: getUserId(),
            profileid: Cookies.get('hem-klaviyo-profile-id'),
          }),
        },
      );

      if (!res.ok) {
        throw new Error(res.statusText);
      }

      if (isFunction(reportEmail)) {
        reportEmail(email);
      }

      setState(State.SUCCESS);
      setName('');
      setEmail('');
      sendGtmNewsletterEvent({ action: 'Newsletter signup' });

      setTimeout(() => {
        setState(State.IDLE);

        if (isFunction(onSuccess)) {
          console.log('onSuccess called in NewsletterForm');
          onSuccess();
        }
      }, 3000);
    } catch (error) {
      console.log(error);
      setState(State.ERROR);
    }
  };

  useEffect(() => {
    if (state === State.SUCCESS) {
      confetti({ colors: ['#2326DE', '#D2DFEF'] });
    }
  }, [state]);

  return (
    <div className={root}>
      <form className={form} {...{ onSubmit }}>
        <Input
          label="Your first name"
          labelSecondary="Optional"
          name="name"
          placeholder={placeholder.first}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-selector="Newsletter form input name"
        />
        <Input
          label="Your email address"
          name="email"
          placeholder={placeholder.email}
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-selector="Newsletter form input email"
        />
        <Button
          type="submit"
          aria-label="Sign up"
          disabled={state === State.LOADING}
          color={state === State.SUCCESS ? 'green' : 'blue'}
          data-selector="Newsletter form button"
          data-selector-location={location}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              className="flex items-center justify-center gap-2"
              initial={{ translateY: -2, opacity: 0 }}
              animate={{ translateY: 0, opacity: 1 }}
              exit={{ translateY: 2, opacity: 0 }}
              key={state}
              {...{ transition }}
            >
              {
                {
                  [State.LOADING]: (
                    <>
                      <LoadingIndicator />
                      Signing up…
                    </>
                  ),
                  [State.IDLE]: <>{buttonLabel}</>,
                  [State.ERROR]: <>{buttonLabel}</>,
                  [State.SUCCESS]: (
                    <>
                      <CheckOutlinedIcon
                        fontSize="small"
                        style={{ fill: 'var(--color-white)' }}
                      />
                      Signed up!
                    </>
                  ),
                }[state]
              }
            </motion.div>
          </AnimatePresence>
        </Button>
      </form>
      {state === State.ERROR ? (
        <p className="text-error mb-4 text-center text-sm">
          Uh-oh! Something went wrong. Please try again later.
        </p>
      ) : null}
      <p className="mt-2 text-balance text-center text-sm text-black-600">
        By signing up to our newsletter you agree to our{' '}
        <Link to="/privacy-policy">Privacy policy</Link>
      </p>
    </div>
  );
};
